import { Component, Inject, OnInit } from '@angular/core';
import { FormModalBaseComponent } from '../../../../../core/base/components/form-modal-base/form-modal-base.component';
import { ContractModel } from '../../../../../core/models/contract.model';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ContractService } from '../contract.service';
import { GenericEngagementService } from '../../../settings/generic-engagement/generic-engagement.service';
import { Observable, of } from 'rxjs';
import { GenericEngagementModel } from '../../../../../core/models/generic-engagement.model';
import { map, catchError } from 'rxjs/operators';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { EstablishmentModel } from '../../../../../core/models/establishment.model';
import { EstablishmentService } from '../../establishment/establishment.service';

@Component({
  selector: 'vex-contract-create-update',
  templateUrl: './contract-create-update.component.html',
  styleUrls: ['./contract-create-update.component.scss'],
})
export class ContractCreateUpdateComponent extends FormModalBaseComponent<ContractModel> implements OnInit {
  genericEngagements$: Observable<GenericEngagementModel[]>;
  supportEstablishment$: Observable<EstablishmentModel[]>;
  hasUsersupportEstablishment = false; // Désactivé par défaut
  showsupportEstablishmentField = false;
  supportEstablishmentList: EstablishmentModel[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<FormModalBaseComponent<ContractModel>>,
    public service: ContractService,
    public genericEngagementService: GenericEngagementService,
    public snackbarService: SnackbarService,
    private establishmentService: EstablishmentService,
  ) {
    super(data, dialogRef, service);
  }

  ngOnInit() {
    this.form = new UntypedFormGroup({
      genericEngagement: new UntypedFormControl(null, [Validators.required]),
      isInternal: new UntypedFormControl(false),
      // Required only when isInternal is true (handled below)
      supportEstablishment: new UntypedFormControl(null),
    });

    this.genericEngagementAutoCompleteChange('');
    this.checksupportEstablishment();

    // Pilotage de l'affichage par la case à cocher "Interne"
    this.form.controls.isInternal.valueChanges.subscribe((checked: boolean) => {
      this.showsupportEstablishmentField = !!checked;
      // Toggle required validator based on isInternal
      const supportCtrl = this.form.controls.supportEstablishment;
      if (checked) {
        supportCtrl.setValidators([Validators.required]);
      } else {
        supportCtrl.clearValidators();
      }
      supportCtrl.updateValueAndValidity();

      if (checked) {
        if (!this.hasUsersupportEstablishment && this.supportEstablishmentList.length === 0) {
          this.snackbarService.warning(
            "La création d'un contrat interne est impossible sans être affecté à au moins un établissement support",
          );
        }
        if (this.supportEstablishmentList.length === 1) {
          this.form.controls.supportEstablishment.setValue(this.supportEstablishmentList[0]);
        }
      } else {
        this.form.controls.supportEstablishment.reset();
      }
    });

    // Ensure correct validator state at init
    const supportCtrl = this.form.controls.supportEstablishment;
    if (this.form.controls.isInternal.value) {
      supportCtrl.setValidators([Validators.required]);
    } else {
      supportCtrl.clearValidators();
    }
    supportCtrl.updateValueAndValidity({ emitEvent: false });
  }

  genericEngagementAutoCompleteChange(state: string = '') {
    this.genericEngagements$ = this.genericEngagementService
      .findAll(null, null, 'label', 'asc', state, {
        inUserPerimeter: true,
        format: 'Contrat',
      })
      .pipe(
        map((x: any) => {
          return x.data.map((engagement: GenericEngagementModel) => ({
            ...engagement,
            displayLabel: engagement.label,
          }));
        }),
      );
  }

  supportEstablishmentAutoCompleteChange(state: string = '') {
    this.supportEstablishment$ = this.establishmentService
      .findAll(null, null, 'label', 'asc', state, { sameEstablishmentsCurrentUser: true, issupportEstablishment: true })
      .pipe(map((x: any) => x.data));
  }

  checksupportEstablishment() {
    this.establishmentService
      .findAll(null, null, 'label', 'asc', '', { sameEstablishmentsCurrentUser: true, issupportEstablishment: true })
      .pipe(
        map((x: any) => {
          const hasSupport = x.data.length > 0;
          this.hasUsersupportEstablishment = hasSupport;
          return x.data;
        }),
        catchError(err => {
          this.hasUsersupportEstablishment = false;
          return of([]);
        }),
      )
      .subscribe(data => {
        this.supportEstablishmentList = data;
        this.supportEstablishment$ = of(data);
      });
  }

  afterCreateItem(result?: any, error?: any) {
    if (result) {
      this.close(true);
    }
  }

  createItem() {
    const { genericEngagement, isInternal } = this.form.getRawValue();
    if (this.form.valid) {
      const payload: any = {
        genericEngagementId: genericEngagement._id.toString(),
        isInternal: !!isInternal,
      };
      if (this.showsupportEstablishmentField) {
        payload.supportEstablishment = this.form.controls.supportEstablishment.value;
      }
      this.service.create(payload).subscribe(res => {
        if (res) {
          this.snackbarService.success('Le contrat a été créé avec succès');
          this.close(res);
        }
      });
    } else {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      this.form.get('genericEngagement').markAsTouched();
      this.form.get('genericEngagement').updateValueAndValidity();
      this.form.get('supportEstablishment').markAsTouched();
      this.form.get('supportEstablishment').updateValueAndValidity();
    }
  }
}
